import type { SparklinePoint } from '../components/MetricSparkline.js';

// Per-day series shipped by GET /api/scorecard (the `daily` array). Kept here,
// DOM-free, so the sparkline-projection contract is unit-testable without
// importing the route module (which pulls in browser-only globals).
export interface DailyMetricsPoint {
  date: string;
  prs_merged: number;
  auto_approval_rate: number | null;
  ttv_p50_seconds: number | null;
  // Optional for back-compat with API builds that pre-date shadow trendlines —
  // older deploys omit the fields and the sparklines render empty.
  shadow_would_approve_count?: number;
  shadow_auto_approval_rate?: number | null;
  // Optional for back-compat with API builds before the findings-acted-on
  // metric. Sparkline renders empty when absent.
  findings_acted_on_rate?: number | null;
  // Optional for back-compat with API builds before the cost sparkline.
  // Sparkline renders empty when absent.
  avg_cost_per_pr?: number | null;
}

// Only metrics whose per-day series the API actually ships (see
// DailyMetricsPoint and projectSeries) belong here. A metric listed here but
// not projectable renders an empty "no data yet" sparkline that contradicts
// its populated tile — so revert/override/ttfr stay out until the API
// emits their daily series. Keep this map and projectSeries in lockstep
// (enforced by velocity-scorecard.projection.test.ts).
export const SPARKLINE_KIND: Record<string, 'line' | 'bar'> = {
  prs_merged: 'bar',
  auto_approval_rate: 'line',
  ttv_avg_seconds: 'line',
  findings_acted_on_rate: 'line',
  avg_cost_per_pr: 'line',
};

export function projectSeries(metric: string, daily: DailyMetricsPoint[]): SparklinePoint[] {
  switch (metric) {
    case 'prs_merged':
      return daily.map((d) => ({ date: d.date, value: d.prs_merged }));
    case 'auto_approval_rate':
      return daily.map((d) => ({ date: d.date, value: d.auto_approval_rate }));
    case 'ttv_avg_seconds':
      return daily.map((d) => ({ date: d.date, value: d.ttv_p50_seconds }));
    case 'findings_acted_on_rate':
      return daily.map((d) => ({ date: d.date, value: d.findings_acted_on_rate ?? null }));
    case 'avg_cost_per_pr':
      return daily.map((d) => ({ date: d.date, value: d.avg_cost_per_pr ?? null }));
    default:
      return [];
  }
}
